import { InputHTMLAttributes, useEffect, useState } from "react";
import { TreeViewComponent } from "components/TreeViewComponent";
import { FieldsSettingsModel } from "@syncfusion/ej2-react-navigations";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import IGroupResponseItem from "interfaces/responses/IGroupResponseItem";

import AmplifyError from "components/AmplifyError/AmplifyError";
import { enableRipple } from "@syncfusion/ej2-base";
import { NoData, Spinner } from "@whitespace/whitespace-ui";

interface GroupsProp extends InputHTMLAttributes<HTMLInputElement> {
  groupsSelected : string[],
  handleGroupSelectionChange : (updatedGroupsChecked : string[]) => void;
  handleIsSending : (sending: boolean) => void;
  handleErrors: (errors: string[]) => void;
}

enableRipple(true);
const Groups = ({
  groupsSelected, handleGroupSelectionChange, handleIsSending, handleErrors
}: GroupsProp) => {
  const [groupList, setGroupList] = useState<IGroupResponseItem[]>();
  const [expandedNodes, setExpandedNodes] = useState<string[]>();
  const [treeIsDisabled, setTreeIsDisabled] = useState(false);

  const [initComplete, setInitComplete] = useState(false);
  const {
    isSending, errors, data, sendRequest
  } = useAmplifyREST();

  let treeObj : any;
  const handleGroupSelectionChangeBase = (args: any) => {
    if (treeObj) {
      handleGroupSelectionChange(treeObj.getAllCheckedNodes());
    }
  };

  const handleNodeChecking = (args:any) => {
    if (treeObj) {
      const treeData = treeObj.getTreeData(args.node);
      const currentNodeId = treeData[0].id;
      const fullTree = treeObj.getTreeData();

      const directChildren = fullTree.filter((node : any) => node.pId === currentNodeId).map((node:any) => node.id);

      if (args.action === "check") {
        treeObj.checkAll(directChildren);
      } else if (args.action === "uncheck") {
        treeObj.uncheckAll(directChildren);
      }
    }
  };

  useEffect(() => {
    handleIsSending(true);
    sendRequest("groups", "GET");
  }, [sendRequest]);

  useEffect(() => {
    if (data) {
      let groupResponse = data as IGroupResponseItem[];
      groupResponse = groupResponse.map(group => {
        group.isExpanded = groupsSelected.includes(group.id);
        group.isChecked = groupsSelected.includes(group.id);
        return group;
      });

      if (groupResponse.length === 1) {
        groupResponse[0].isChecked = true;
        setTreeIsDisabled(true);
        handleGroupSelectionChange([groupResponse[0].id]);
      }

      setGroupList(groupResponse);
      handleIsSending(false);
    }
  }, [data]);

  useEffect(() => {
    if (errors) {
      handleErrors(errors);
    }
  }, [errors]);

  useEffect(() => {
    if (groupList) {
      let groupsExpanded = groupList;
      let parentsToBeExpanded = groupList.filter(
        group => groupsSelected.includes(group.id)
      && groupList.find(grp => grp.id === group.pId && !grp.isExpanded)
      ).map(group => group.pId);

      if (parentsToBeExpanded) {
        while (parentsToBeExpanded.length !== 0) {
          // eslint-disable-next-line no-loop-func
          groupsExpanded = groupsExpanded?.map(group => {
            if (parentsToBeExpanded.includes(group.id)) {
              group.isExpanded = true;
            }
            return group;
          });

          const newsParentsToBeExpanded = groupList.filter(
            // eslint-disable-next-line no-loop-func
            group => parentsToBeExpanded.includes(group.id)
          && groupList.find(grp => grp.id === group.pId && !grp.isExpanded)
          ).map(group => group.pId);

          parentsToBeExpanded = newsParentsToBeExpanded;
        }
      }
      setExpandedNodes(groupsExpanded.filter(group => group.isExpanded).map(group => group.id));
      setInitComplete(true);
    }
  }, [groupList]);

  const fieldSettings : FieldsSettingsModel = {
    dataSource: groupList as any, id: "id", text: "name", parentID: "pId", hasChildren: "hasChild"
  };

  return (
    <>
      {errors && <AmplifyError errors={errors} />}
      <Spinner animation="grow" visible={isSending} spinnerText="Loading" variant="info" />
      {initComplete && <NoData visible={groupList!.length === 0} description="No Groups Found" /> }
      {initComplete && groupList!.length > 0
        && (
          <TreeViewComponent
            ref={scope => { treeObj = scope; }}
            fields={fieldSettings}
            showCheckBox
            expandedNodes={expandedNodes}
            loadOnDemand={false}
            nodeChecked={handleGroupSelectionChangeBase}
            disabled={isSending || treeIsDisabled}
            autoCheck={false}
            nodeChecking={handleNodeChecking}
          />
        )}
    </>
  );
};

export default Groups;
